// axiosInstance.js
import axios from "axios";
import Cookies from "js-cookie";
import { baseUrl } from "../config/config";

const axiosInstance = axios.create({
  baseURL: baseUrl,
});

// ---------------------------------------------------------------------------
// Public endpoints MUST NOT have Authorization attached — candidate-facing,
// token-bearing URLs, login, OTP. Substring matching; tolerates query strings.
// ---------------------------------------------------------------------------
const PUBLIC_SKIP_LIST = [
  "/api/auth/token",
  "/api/auth/refresh",
  "/api/auth/",
  "/ai/invite/public/",
  "/ai/interview/public/",
  "/ai-interview/",
  // "/invite/",
  "/interview-link-validation",
  "/get-language-for-interview",
  "/create-room-and-generate-token",
  "/get-all-questions",
  "/save-candidate-response",
  "/candidate-interview-status",
  "/common/user/fetch",
  "/common/user/save",
  "/common/otp/generate-otp",
  "/common/otp/send-whatsapp-otp",
  "/common/otp/verify",
];

const isPublicEndpoint = (url = "") =>
  PUBLIC_SKIP_LIST.some((pat) => url.includes(pat));

// ---------------------------------------------------------------------------
// Token helpers
// ---------------------------------------------------------------------------
const ACCESS_KEY = "e_access_token";
const REFRESH_KEY = "e_refresh_token";
// Refresh this many ms before the JWT's exp claim — avoids firing with an already-expired token.
const PROACTIVE_REFRESH_WINDOW_MS = 60 * 1000;

const getAccessToken = () =>
  localStorage.getItem(ACCESS_KEY) || Cookies.get(ACCESS_KEY) || null;

const getRefreshToken = () =>
  localStorage.getItem(REFRESH_KEY) || Cookies.get(REFRESH_KEY) || null;

const setAccessToken = (t) => {
  if (t) localStorage.setItem(ACCESS_KEY, t);
};

const setRefreshToken = (t) => {
  if (t) localStorage.setItem(REFRESH_KEY, t);
};

const clearTokens = () => {
  try {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    Cookies.remove(ACCESS_KEY);
    Cookies.remove(REFRESH_KEY);
  } catch (_) {
    // storage blocked (Safari private mode) — fall through
  }
};

// Client-side JWT exp decode. No signature verification; backend still rejects tampered tokens.
const getTokenExpMs = (token) => {
  if (!token) return 0;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return typeof payload.exp === "number" ? payload.exp * 1000 : 0;
  } catch {
    return 0;
  }
};

const tokenNeedsRefresh = (token) => {
  const exp = getTokenExpMs(token);
  if (!exp) return false; // opaque token — rely on 401 reactive refresh instead
  return Date.now() >= exp - PROACTIVE_REFRESH_WINDOW_MS;
};

// ---------------------------------------------------------------------------
// Session validity vs. endpoint rejection.
//
// A 401 does NOT prove the session is dead — an endpoint can reject a perfectly
// valid token (missing permission, tenant feature disabled, backend bug). Wiping
// tokens on every 401 put users in a signin loop: log in -> dashboard fires the
// failing call -> logged out -> log in again -> same call -> logged out.
//
// We only tear down the session when we can corroborate it: the access token is
// gone, or its exp claim has passed. Opaque tokens (no decodable exp) can't be
// judged, so they fall back to the consecutive-401 counter below.
// ---------------------------------------------------------------------------
// "dead" | "alive" | "unknown"
const getSessionState = () => {
  const token = getAccessToken();
  if (!token) return "dead";
  const exp = getTokenExpMs(token);
  if (!exp) return "unknown"; // opaque token — defer to the counter
  return Date.now() >= exp ? "dead" : "alive";
};

// Fallback for opaque tokens: a lone 401 is an endpoint saying no, but several in
// a row across different URLs means the session really is gone.
const CONSECUTIVE_401_LOGOUT_THRESHOLD = 3;
let consecutive401Urls = new Set();

const noteAuthFailure = (url) => {
  consecutive401Urls.add(url);
  return consecutive401Urls.size >= CONSECUTIVE_401_LOGOUT_THRESHOLD;
};

const resetAuthFailures = () => {
  if (consecutive401Urls.size) consecutive401Urls = new Set();
};

// ---------------------------------------------------------------------------
// Refresh coordination — concurrent 401s share ONE refresh call.
// Queued subscribers wake up with the new token (or null on failure).
// ---------------------------------------------------------------------------
let refreshPromise = null;

const refreshAccessToken = () => {
  if (refreshPromise) return refreshPromise;

  const refreshToken = getRefreshToken();
  if (!refreshToken) return Promise.resolve(null);

  // Plain axios (not axiosInstance) so the refresh call itself doesn't re-enter this interceptor.
  refreshPromise = axios
    .post(
      `${baseUrl}/api/auth/refresh`,
      { refreshToken },
      { headers: { "Content-Type": "application/json" } },
    )
    .then((resp) => {
      const newAccess = resp?.data?.token || resp?.data?.accessToken;
      const newRefresh = resp?.data?.refreshToken;
      if (!newAccess) throw new Error("refresh response missing token");
      setAccessToken(newAccess);
      if (newRefresh) setRefreshToken(newRefresh);
      return newAccess;
    })
    .catch(() => null)
    .finally(() => {
      // Always release the lock — next 401 will either refresh again or fall through to redirect.
      refreshPromise = null;
    });

  return refreshPromise;
};

// ---------------------------------------------------------------------------
// Redirect guard — collapses a storm of concurrent 401s into a single nav.
// ---------------------------------------------------------------------------
let isRedirecting = false;

// Where to send the user after they sign back in. Read + cleared by auth-callback.
export const POST_LOGIN_REDIRECT_KEY = "post_login_redirect";

const redirectToSignin = () => {
  if (isRedirecting) return;
  isRedirecting = true;
  clearTokens();
  // Remember where they were so the OAuth callback can send them back instead of
  // dumping them on the default landing route. sessionStorage (not localStorage)
  // because the signin screen clears localStorage on mount, and it must survive
  // the round-trip through the external auth server.
  const attempted = `${window.location.pathname}${window.location.search}`;
  try {
    if (attempted.startsWith("/admin") || attempted.startsWith("/user")) {
      sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, attempted);
    }
  } catch (_) {
    // storage blocked — user just lands on the default route
  }
  setTimeout(() => {
    if (!window.location.pathname.startsWith("/signin")) {
      window.location.href = "/signin";
    }
  }, 0);
};

// ---------------------------------------------------------------------------
// Forced password change — while the user's mustChangePassword flag is set the
// server rejects every gated call with 403 { code: "PASSWORD_CHANGE_REQUIRED" }.
// Route them to the change-password screen; the two /me/* routes are allow-listed
// server-side so the screen can still call them.
// ---------------------------------------------------------------------------
let isRedirectingToChangePw = false;

const redirectToChangePassword = () => {
  if (isRedirectingToChangePw) return;
  if (window.location.pathname === "/change-password") return;
  isRedirectingToChangePw = true;
  setTimeout(() => {
    if (window.location.pathname !== "/change-password") {
      window.location.href = "/change-password";
    }
    isRedirectingToChangePw = false;
  }, 0);
};

// ---------------------------------------------------------------------------
// Request interceptor
//   * skip auth for public endpoints (fixes "Bearer undefined")
//   * proactive refresh when the JWT is within the expiry window
//   * attach Bearer only when we have a truthy token
// ---------------------------------------------------------------------------
axiosInstance.interceptors.request.use(
  async (config) => {
    const url = config?.url || "";

    if (isPublicEndpoint(url)) {
      return config;
    }

    let accessToken = getAccessToken();

    if (accessToken && tokenNeedsRefresh(accessToken)) {
      const refreshed = await refreshAccessToken();
      if (refreshed) accessToken = refreshed;
    }

    if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ---------------------------------------------------------------------------
// A 401 we can't recover from by refreshing. Decide: dead session (log out) or
// a single endpoint refusing us (surface the error, keep the user logged in).
// ---------------------------------------------------------------------------
const handleUnrecoverable401 = (requestUrl) => {
  const state = getSessionState();
  // A decodably-valid token is trusted outright — the counter is only for opaque
  // tokens, where repeated 401s across distinct endpoints are our only signal.
  const sessionDead =
    state === "dead" ||
    (state === "unknown" && noteAuthFailure(requestUrl));

  if (sessionDead) {
    redirectToSignin();
    return Promise.reject({
      status: 401,
      message: "Your session has expired. Please sign in again.",
      sessionExpired: true,
    });
  }

  // Token still valid by its own exp — this endpoint rejected us specifically.
  console.warn(
    `[auth] 401 from ${requestUrl} but the session token is still valid; ` +
      `not signing out. Check permissions for this endpoint.`,
  );
  return Promise.reject({
    status: 401,
    message: "You do not have access to this resource.",
    sessionExpired: false,
  });
};

// ---------------------------------------------------------------------------
// Response interceptor
//   * on 401 (non-public): try refresh once; on success retry; on failure redirect
//   * single redirect guarded by isRedirecting; no retry storm
//   * normalized error shapes for 400/403/500/network
// ---------------------------------------------------------------------------
axiosInstance.interceptors.response.use(
  (response) => {
    // Any authenticated call succeeding proves the session is alive.
    resetAuthFailures();
    return response;
  },
  async (error) => {
    const status = error.response?.status;
    const originalRequest = error.config || {};
    const requestUrl = originalRequest.url || "";

    // 401 on a public endpoint is a legit backend rejection — bubble up, don't refresh.
    if (
      status === 401 &&
      !isPublicEndpoint(requestUrl) &&
      !originalRequest._retried
    ) {
      originalRequest._retried = true;

      const refreshed = await refreshAccessToken();

      if (refreshed) {
        // Retry the original request with the new token.
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers["Authorization"] = `Bearer ${refreshed}`;
        resetAuthFailures();
        return axiosInstance(originalRequest);
      }

      // Refresh unavailable (no refresh token issued at login yet). Only end the
      // session if it's genuinely dead — otherwise this endpoint alone rejected us.
      return handleUnrecoverable401(requestUrl);
    }

    // 401 that's already been retried once, or on a public endpoint — stop here.
    if (status === 401) {
      // A public endpoint returning 401 is a legit backend rejection, not a
      // session problem — never log out for it.
      if (isPublicEndpoint(requestUrl)) {
        return Promise.reject({ status: 401, message: "Unauthorized" });
      }
      return handleUnrecoverable401(requestUrl);
    }

    if (status === 400) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Bad request. Please check your input.";
      return Promise.reject({
        status: 400,
        message: errorMessage,
        data: error.response?.data,
      });
    }

    if (status === 403) {
      // First-login password gate — bounce to the change-password screen.
      if (error.response?.data?.code === "PASSWORD_CHANGE_REQUIRED") {
        redirectToChangePassword();
        return Promise.reject({
          status: 403,
          code: "PASSWORD_CHANGE_REQUIRED",
          message:
            error.response?.data?.message ||
            "You must change your password before continuing.",
          data: error.response?.data,
        });
      }
      // 403 = Forbidden (authenticated but no permission).
      // Let individual components handle it (show an error/empty state).
      return Promise.reject({
        status: 403,
        message: "Forbidden",
        data: error.response?.data,
      });
    }

    if (status === 500) {
      return Promise.reject({
        status: 500,
        message: "Internal server error. Please try again later.",
      });
    }

    if (!error.response) {
      return Promise.reject({
        status: 0,
        message: "Network error. Please check your internet connection.",
      });
    }

    return Promise.reject({
      status: status || 0,
      message:
        error.response?.data?.message ||
        error.message ||
        "An unexpected error occurred.",
      data: error.response?.data,
    });
  },
);

// Expose refresh-token setter so the signin flow can stash a refresh token once the backend issues one.
// Usage from the signin slice: import { setRefreshToken } from "../interceptors";
export { setRefreshToken };
export default axiosInstance;
